import { some } from 'lodash';
import {
  InteractionStepType,
  InteractionStepConfig,
  IndexedInteractionStepResponse,
  StepParamsForType,
  StepParamsForTypeWithStage,
} from '@scale/shared/types/chat';
import { ReviewLevel } from '@scale/shared-types';
import {
  isEmptyResponse,
  createDefaultResponseForFields,
  validateRequiredResponses,
} from '@scale/shared/textcollection/helpers';
import { IMultiTurnThreadHistory } from 'react-scale/components/scalers/tasks/ThreadedInteraction/ThreadHistory.interface';
import { isConditionallyVisible, isStepVisible } from '@scale/shared/chat/api';
import TextCollectionViewComponent from './TextCollectionViewComponent';
import TextCollectionPinnedViewComponent from './TextCollectionPinnedViewComponent';

import { TextCollectionConfigurationComponent } from './TextCollectionConfigurationComponent';

import { schema } from './validation';
import StepDescriptor from '../stepDescriptor/StepDescriptor';
import { registerStepDescriptor } from '../stepDescriptor/stepDescriptorContainer';
import tcGetResponseTemplatePaths from './tcGetResponseTemplatePaths';

export class TextCollectionDescriptor extends StepDescriptor<InteractionStepType.TextCollection> {
  type = InteractionStepType.TextCollection;
  title = InteractionStepConfig[InteractionStepType.TextCollection].title;
  description = InteractionStepConfig[InteractionStepType.TextCollection].description;

  validationSchema = schema;

  ViewComponent = TextCollectionViewComponent;
  PinnedViewComponent = TextCollectionPinnedViewComponent;

  ConfigurationComponent = TextCollectionConfigurationComponent;

  isResponseValid(
    response: IndexedInteractionStepResponse,
    stepParams: StepParamsForTypeWithStage<InteractionStepType.TextCollection>,
    history: IMultiTurnThreadHistory,
    reviewLevel?: ReviewLevel,
  ) {
    if (
      !isStepVisible(stepParams, reviewLevel) ||
      !isConditionallyVisible(
        stepParams,
        history.responses,
        response.index,
        history.threadParams.templateVariables,
        reviewLevel,
      )
    ) {
      return { canSubmit: true, submitTooltip: '' };
    }
    const fields = stepParams.params.fields;
    const annotations = response?.context?.response?.annotations ?? {};
    const requiresNonEmptyResponse = some(fields, field => field.required);
    const { isValid: responseContainsAllRequiredFields } = validateRequiredResponses(
      fields,
      annotations,
      history.threadParams.templateVariables,
    );

    if (
      requiresNonEmptyResponse &&
      (isEmptyResponse(response?.context?.response) || !responseContainsAllRequiredFields)
    ) {
      return { canSubmit: false, submitTooltip: 'Some steps have not been completed.' };
    }

    return { canSubmit: true, submitTooltip: '' };
  }

  getResponseTemplatePaths(
    stepParams: StepParamsForType<InteractionStepType.TextCollection>,
    _history: IMultiTurnThreadHistory,
  ) {
    return tcGetResponseTemplatePaths(stepParams.params.fields);
  }

  generateEmptyResponse(
    stepParams: StepParamsForType<InteractionStepType.TextCollection>,
    history: IMultiTurnThreadHistory,
  ) {
    const emptyResponse = createDefaultResponseForFields(stepParams.params.fields);
    return {
      type: InteractionStepType.TextCollection,
      context: {
        response: emptyResponse,
      },
      output: history.getMostRecentResponse(),
    };
  }
}

registerStepDescriptor({
  type: InteractionStepType.TextCollection,
  descriptor: new TextCollectionDescriptor(),
});
